import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'

import { useLeadershipProfile } from 'global/hooks/useLeadershipProfiles'

import Button from 'components/Button'

// ~10 minutes per meeting is a realistic average across the 10 competencies
// times one Claude call per (competency, meeting). Used both for copy and
// for the progress bar estimate while the worker runs.
const MINUTES_PER_MEETING = 10

const STATUS_COPY = {
  queued: {
    title: 'Queued',
    body:
      'Your profile is waiting to start. This usually takes just a moment.'
  },
  running: {
    title: 'Analyzing your meetings',
    body:
      'We’re pulling each transcript from your source and running it through our leadership rubric. About 10 minutes per meeting — feel free to close this tab, we’ll save your profile when it’s done.'
  },
  complete: {
    title: 'Profile ready',
    body:
      'Your leadership profile is ready. Open it to see your scores, strengths, and growth edges.'
  },
  failed: {
    title: 'Something went wrong',
    body:
      'We couldn’t finish generating your profile. The transcripts are still safe — you can try again.'
  }
}

const formatElapsed = (start, now) => {
  if (!start) return null
  const seconds = Math.max(
    0,
    Math.floor((now - new Date(start).getTime()) / 1000)
  )
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  const rem = seconds % 60
  return rem ? `${minutes}m ${rem}s` : `${minutes}m`
}

const GenerationProgress = ({
  profileId,
  onStartOver,
  onViewProfile
}) => {
  const { profile, error } = useLeadershipProfile(profileId, {
    pollIntervalMs: 5000
  })

  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const isTerminal =
      profile &&
      (profile.status === 'complete' ||
        profile.status === 'failed')
    if (isTerminal) return undefined
    const interval = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [profile])

  if (error) {
    return (
      <div className="p-20 border rounded">
        <p className="font-weight-bold mb-5">
          Couldn’t load profile status
        </p>
        <p className="text-gray-dark mb-15">
          {error.message ||
            'We hit a snag checking on your profile. It may still be running in the background.'}
        </p>
        {onStartOver && (
          <Button
            variant="link"
            smallPadding
            onClick={onStartOver}
          >
            Start over
          </Button>
        )}
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="p-20 border rounded">
        <p className="mb-0">Checking status…</p>
      </div>
    )
  }

  const copy = STATUS_COPY[profile.status] || STATUS_COPY.queued
  const elapsed = formatElapsed(
    profile.startedAt || profile.createdAt,
    now
  )
  const startedLabel =
    profile.startedAt || profile.createdAt
      ? moment(profile.startedAt || profile.createdAt).format(
          'MMM D, h:mm A'
        )
      : null

  if (profile.status === 'complete') {
    return (
      <div className="p-20 border rounded bg-gray-light">
        <p
          className="font-weight-bold mb-5"
          style={{ fontSize: 16 }}
        >
          {copy.title}
        </p>
        <p className="text-gray-dark mb-15">{copy.body}</p>
        <div className="d-flex align-items-center">
          {onViewProfile && (
            <Button
              variant="secondary"
              smallPadding
              onClick={() => onViewProfile(profile.id)}
            >
              View profile
            </Button>
          )}
          {onStartOver && (
            <Button
              variant="link"
              smallPadding
              onClick={onStartOver}
              className="ml-15"
            >
              Start another
            </Button>
          )}
        </div>
      </div>
    )
  }

  if (profile.status === 'failed') {
    return (
      <div className="p-20 border rounded">
        <p
          className="font-weight-bold mb-5"
          style={{ fontSize: 16 }}
        >
          {copy.title}
        </p>
        <p className="text-gray-dark mb-10">{copy.body}</p>
        {profile.error && (
          <p
            className="text-danger mb-15"
            style={{ fontSize: 13 }}
          >
            {profile.error}
          </p>
        )}
        {onStartOver && (
          <Button
            variant="secondary"
            smallPadding
            onClick={onStartOver}
          >
            Try again
          </Button>
        )}
      </div>
    )
  }

  // Prefer the worker-reported progress when present (one unit of work per
  // competency × meeting pair, persisted on each event). Older profiles
  // generated before the progressPercent column existed fall back to the
  // time-based estimate.
  const realPercent = Number(profile.progressPercent)
  const hasRealProgress = Number.isFinite(realPercent)
  let progressPercent
  if (hasRealProgress) {
    progressPercent = Math.max(0, Math.min(100, realPercent))
  } else {
    const expectedMs =
      (profile.meetingCount || 1) *
      MINUTES_PER_MEETING *
      60 *
      1000
    const startMs = profile.startedAt
      ? new Date(profile.startedAt).getTime()
      : profile.createdAt
      ? new Date(profile.createdAt).getTime()
      : null
    const elapsedMs = startMs ? Math.max(0, now - startMs) : 0
    progressPercent =
      expectedMs > 0
        ? Math.min(
            95,
            Math.round((elapsedMs / expectedMs) * 100)
          )
        : 0
  }

  return (
    <div className="p-20 border rounded">
      <p
        className="font-weight-bold mb-5"
        style={{ fontSize: 16 }}
      >
        {copy.title}
      </p>
      <p className="text-gray-dark mb-15">{copy.body}</p>

      <div className="d-flex align-items-center mb-10">
        <div
          className="flex-grow-1 mr-15"
          style={{
            height: 8,
            backgroundColor: '#E5E9EE',
            borderRadius: 4,
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              width: `${progressPercent}%`,
              height: '100%',
              backgroundColor: '#3F8CFF',
              transition: 'width 0.5s ease'
            }}
          />
        </div>
        <span
          className="font-weight-bold"
          style={{
            fontSize: 13,
            color: '#3F8CFF',
            minWidth: 40,
            textAlign: 'right'
          }}
        >
          {progressPercent}%
        </span>
      </div>

      <p
        className="text-gray-dark mb-0"
        style={{ fontSize: 13 }}
      >
        {startedLabel ? `Started ${startedLabel}` : null}
        {elapsed ? ` · ${elapsed} elapsed` : null}
        {' · '}
        {profile.meetingCount}{' '}
        {profile.meetingCount === 1 ? 'meeting' : 'meetings'}
      </p>
    </div>
  )
}

GenerationProgress.propTypes = {
  profileId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]).isRequired,
  onStartOver: PropTypes.func,
  onViewProfile: PropTypes.func
}

export default GenerationProgress
